import React from 'react';
import { motion } from 'framer-motion';
import './AboutSection.css';

const AboutSection = () => {
  const stats = [
    { number: '20+', label: 'Years Experience', icon: '🏗️' },
    { number: '₹5,500+', label: 'Crores in Transactions', icon: '💰' },
    { number: '10+', label: 'Cities Served', icon: '🌆' },
    { number: '100+', label: 'Successful Projects', icon: '⭐' }
  ];

  const values = [
    {
      icon: '🤝',
      title: 'Client-Centric Approach',
      description: 'Commitment to customer satisfaction with transparent communication and delivery.'
    },
    {
      icon: '🏗️',
      title: 'End-to-End Solutions',
      description: 'Comprehensive services from land acquisition to project handover.'
    },
    {
      icon: '🏛️',
      title: 'Regulatory Excellence',
      description: 'Deep understanding of Indian construction laws, RERA compliance, and government procedures.'
    },
    {
      icon: '💰',
      title: 'Financial Strength',
      description: 'Track record of managing ₹5,500+ Crores in transactions with zero defaults.'
    }
  ];

  const milestones = [
    { year: '2004', title: 'Founded', description: 'Vibha Corporation & Co. established by Mr. & Mrs. Sundar Rajan Jagannathan' },
    { year: '2010', title: 'Radiance Realty', description: 'COO role at Radiance Realty Developers India Ltd.' },
    { year: '2013', title: 'CBRE Leadership', description: 'Head of Industrial & Investment Vertical at CBRE Chennai' },
    { year: '2015', title: '₹5,500 Crores', description: 'Successfully managed transactions worth ₹5,500+ Crores' },
    { year: '2021', title: 'NDR Consultancy', description: 'Current consultant for NDR Warehousing managing 16 Million Sq. Ft portfolio' },
    { year: '2024', title: 'Leadership', description: 'Board Chairman of Vibha Listens India Private Limited' }
  ];

  return (
    <section className="about-section">
      <div className="about-container">
        {/* Hero Section */}
        <motion.div
          className="about-hero"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="about-hero-content">
            <motion.h2
              className="about-title"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              About <span className="highlight">Vibha Corporation</span>
            </motion.h2>

            <motion.p
              className="about-subtitle"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              Vibha Corporation & Co. is a partnership firm founded by Mr. & Mrs. Sundar Rajan Jagannathan, built on the pillars of integrity, expertise, and customer-centricity. With operations primarily in Chennai and select cities across Tamil Nadu, we have established ourselves as a trusted name in the construction and real estate industry.
            </motion.p>

            <motion.div
              className="about-cta"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <button 
                className="btn-secondary"
                onClick={() => {
                  const leadershipSection = document.getElementById('leadership');
                  if (leadershipSection) {
                    leadershipSection.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }
                }}
              >
                Leadership
              </button>
            </motion.div>
          </div>

          <motion.div
            className="about-hero-image"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="image-container">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=800&fit=crop"
                alt="Vibha Corporation"
                className="hero-image"
              />
              <div className="image-overlay"></div>
            </div>
          </motion.div>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          className="stats-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="stat-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Mission & Values */}
        <motion.div
          className="mission-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="mission-content">
            <motion.div
              className="mission-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="section-title">Our Vision</h3>
              <p className="mission-description">
                To be Tamil Nadu's most trusted construction and Real Estate Services Company, delivering exceptional value through innovative solutions and unwavering commitment to quality.
              </p>

              <h4 className="section-title" style={{marginTop: '2rem'}}>Our Mission</h4>
              <p className="mission-description">
                To serve our customers with utmost care and responsibility, ensuring delight and satisfaction while achieving all stakeholder objectives through transparent, efficient, and sustainable construction practices.
              </p>

              <div className="values-grid">
                {values.map((value, index) => (
                  <motion.div
                    key={value.title}
                    className="value-card"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="value-icon">{value.icon}</div>
                    <h4 className="value-title">{value.title}</h4>
                    <p className="value-description">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="mission-image"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="image-container">
                <img
                  src="/images/vision.png"
                  alt="Our Vision & Mission"
                  className="mission-img"
                />
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Legacy Section */}
        {/* <motion.div
          className="timeline-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="section-title text-center">Our Legacy</h3>
          <div className="legacy-content">
            <p className="legacy-description">
              Our founder, SRJ, brings exceptional credentials to Vibha Corporation:
            </p>
            <ul className="legacy-list">
              <li>20+ Years of industry leadership experience</li>
              <li>Former Chief Operating Officer at Radiance Realty Developers India Ltd.</li>
              <li>Former Head of Land Services at CB Richard Ellis (CBRE) Chennai</li>
              <li>Successfully managed transactions worth ₹5,500+ Crores</li>
              <li>Current consultant for NDR Warehousing managing 16 Million Sq. Ft portfolio</li>
            </ul>
          </div>
        </motion.div> */}

        {/* Commitment Statement */}
        {/* <motion.div
          className="cta-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="cta-content">
            <h3 className="cta-title">Our Commitment</h3>
            <p className="cta-description">
              "At Vibha Corporation, we don't just build structures; we build relationships, trust, and lasting value. Every project is a testament to our commitment to excellence and our promise to deliver beyond expectations."
            </p>
            <p className="cta-description" style={{marginTop: '1rem'}}>
              Ready to start your next project? Contact us today for a consultation.
            </p>
            <div className="cta-buttons">
              <button className="btn-primary">Get Consultation</button>
              <button className="btn-secondary">Contact Us</button>
            </div>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default AboutSection;